import { useRef, useState, KeyboardEvent, useEffect } from "react";
import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  Box,
  Text,
  VStack,
  Divider,
  HStack,
  Avatar,
  Spinner,
  useDisclosure,
  Input,
  InputGroup,
  InputLeftElement,
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";
import React from "react";
import { useAtomValue } from "jotai";
import { AccountAtom, SidebarStateAtom } from "../../store";
import useAccountController from "./useAccountController";
import { IoRadioButtonOff, IoRadioButtonOn } from "react-icons/io5";
import { FiSettings, FiChevronDown } from "react-icons/fi";
import { MangageAccounts } from "../ManageAccounts/ManageAccounts";
import { Show } from "../misc";
import { Account } from "../../types/Account";
import { track, EVENTS } from "../../utils/analytics";

interface AccountSelectorProps {
  className?: string;
}

export default function AccountSelector({
  className = "tutorial-account-manager",
}: AccountSelectorProps) {
  const controller = useAccountController();
  const isCollapsed = useAtomValue(SidebarStateAtom);
  const accountState = useAtomValue(AccountAtom);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [focusIndex, setFocusIndex] = useState(0);
  const accountButtonsRef = useRef<Array<HTMLButtonElement | null>>([]);

  const filteredAccounts = accountState.accounts.filter((account) =>
    account.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Track when the account selector is opened
  useEffect(() => {
    if (isOpen) {
      track(EVENTS.NAVIGATION.MODAL_OPENED, {
        modal_type: "account_selector",
        account_count: accountState.accounts.length,
      });
    }
  }, [isOpen, accountState.accounts.length]);

  // Track search when there's actual input
  useEffect(() => {
    if (isOpen && searchQuery.length > 2) {
      track(EVENTS.SEARCH.SEARCH_PERFORMED, {
        search_term: searchQuery,
        context: "account_selector",
        results_count: filteredAccounts.length,
      });
    }
  }, [searchQuery, isOpen, filteredAccounts.length]);

  const handleAccountSelection = (account: Account) => {
    // Track account selection
    track(EVENTS.NAVIGATION.PAGE_NAVIGATED, {
      action: "account_selected",
      account_id: account.id,
      account_name: account.name,
      via_search: searchQuery.length > 0,
    });

    setIsLoading(true);
    Promise.resolve(controller.setSelectedAccount(account))
      .then(() => {
        setIsLoading(false);
        onClose();
        setSearchQuery("");
      })
      .catch(() => {
        setIsLoading(false);

        // Track failure
        track(EVENTS.ERROR.API_ERROR, {
          action: "account_selection_failed",
          account_id: account.id,
        });
      });
  };

  const handleKeyDown = (
    e: KeyboardEvent<HTMLButtonElement | HTMLInputElement>,
    index?: number
  ) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      const nextIndex =
        typeof index === "number" ? (index + 1) % filteredAccounts.length : 0;
      setFocusIndex(nextIndex);
      accountButtonsRef.current[nextIndex]?.focus();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      const prevIndex =
        typeof index === "number"
          ? (index - 1 + filteredAccounts.length) % filteredAccounts.length
          : filteredAccounts.length - 1;
      setFocusIndex(prevIndex);
      accountButtonsRef.current[prevIndex]?.focus();
    }
  };

  return (
    <>
      <Button
        className={className}
        bg={"var(--dark-gray)"}
        onClick={() => {
          onOpen();
          // Track account selector button clicked using an existing navigation event
          track(EVENTS.NAVIGATION.PAGE_NAVIGATED, {
            element: "account_selector_button",
            action: "clicked",
          });
        }}
        width="240px"
        justifyContent="space-between"
        height="60px"
        px="8px"
        mt="2px"
        position="relative"
        cursor="pointer"
        _hover={{
          bg: "var(--gray)",
          transform: "translateY(-1px)",
          boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
        }}
        _active={{
          transform: "translateY(0)",
        }}
        transition="all 0.2s ease-in-out"
        border="1px solid transparent"
        _focusVisible={{
          border: "1px solid var(--accent)",
          boxShadow: "0 0 0 2px rgba(59, 130, 246, 0.3)",
        }}
      >
        <HStack spacing={3} flex={1}>
          <Box
            bg="var(--gray)"
            borderRadius={"6px"}
            minWidth="46px"
            height="46px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            position="relative"
          >
            {isLoading ? (
              <Spinner size="md" color="var(--white)" />
            ) : (
              <Text fontSize="2xl">
                {accountState?.selectedAccount?.name?.[0]?.toUpperCase() || "?"}
              </Text>
            )}
          </Box>
          {isCollapsed ? null : (
            <VStack w="full" alignItems={"flex-start"} pl="4px">
              <Text fontSize="xs" fontWeight={400} color="var(--text-secondary)">
                Selected Portfolio
              </Text>
              <Text
                maxW="120px"
                overflow={"hidden"}
                textOverflow={"ellipsis"}
                whiteSpace="nowrap"
                fontWeight={500}
              >
                {accountState?.selectedAccount?.name || "Select a portfolio"}
              </Text>
            </VStack>
          )}
        </HStack>
        
        {!isCollapsed && (
          <HStack spacing={1} opacity={0.7}>
            <FiChevronDown size={16} />
          </HStack>
        )}
      </Button>

      <Modal
        isOpen={isOpen}
        onClose={() => {
          onClose();
          // Track when modal is closed
          track(EVENTS.NAVIGATION.MODAL_CLOSED, {
            modal_type: "account_selector",
          });
        }}
        motionPreset="slideInBottom"
      >
        <ModalOverlay backdropFilter="blur(4px)" />
        <ModalContent
          bg="var(--bg)"
          maxW="500px"
          mt="120px"
          boxShadow="xl"
          borderRadius="xl"
        >
          <VStack align="stretch" p={4} spacing={4}>
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.500" />
              </InputLeftElement>
              <Input
                placeholder="Search portfolios..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                autoFocus
                border="none"
                fontSize="lg"
                _focus={{
                  boxShadow: "none",
                  borderColor: "transparent",
                }}
              />
            </InputGroup>

            <Box maxH="400px" overflowY="auto">
              {filteredAccounts.map((account, index) => (
                <React.Fragment key={account.id}>
                  <Button
                    p="3"
                    variant="ghost"
                    justifyContent="space-between"
                    onClick={() => handleAccountSelection(account)}
                    width="full"
                    mb="2"
                    bg={"var(--bg)"}
                    overflow={"hidden"}
                    role="option"
                    aria-selected={
                      account.id === accountState.selectedAccount?.id
                    }
                    ref={(el) => {
                      if (accountButtonsRef.current) {
                        accountButtonsRef.current[index] = el;
                      }
                    }}
                    onKeyDown={(e) => handleKeyDown(e, index)}
                    _hover={{ bg: "var(--gray)" }}
                    _focus={{
                      boxShadow: "0 0 0 2px var(--white)",
                      bg: "var(--gray)",
                    }}
                    transition="all 0.2s"
                  >
                    <HStack>
                      <Avatar
                        bg={"var(--gray)"}
                        name={account.name}
                        size={"sm"}
                        borderRadius={6}
                        color={"var(--white)"}
                      />
                      <Text
                        overflow={"hidden"}
                        textOverflow={"ellipsis"}
                        maxW="300px"
                        whiteSpace="nowrap"
                      >
                        {account.name}
                      </Text>
                    </HStack>

                    <Show if={account.id === accountState.selectedAccount?.id}>
                      <IoRadioButtonOn
                        color="var(--white)"
                        aria-hidden="true"
                      />
                    </Show>
                    <Show if={account.id !== accountState.selectedAccount?.id}>
                      <IoRadioButtonOff
                        color="var(--white)"
                        aria-hidden="true"
                      />
                    </Show>
                  </Button>
                  <Divider mb="2" />
                </React.Fragment>
              ))}
            </Box>

            <MangageAccounts />
          </VStack>
        </ModalContent>
      </Modal>
    </>
  );
}
